import React, { useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Mail, Phone, ScrollText, Megaphone, LineChart, BookOpen } from "lucide-react";
import { SEO } from "../components/SEO";

const desks = [
  {
    key: "editorial",
    icon: ScrollText,
    name: "Editorial & Rights",
    line: "For submissions, translations, IP licensing, foreign rights, and academic partnerships.",
    emails: ["editorial@conlangbookhouse.com", "conlangbookhouse@gmail.com"],
  },
  {
    key: "publicity",
    icon: Megaphone,
    name: "Publicity",
    line: "For interviews, press review copies, launch events, and podcast requests.",
    emails: ["publicity@conlangbookhouse.com", "conlangbookhouse@gmail.com"],
  },
  {
    key: "sales",
    icon: LineChart,
    name: "Sales, Marketing & Promotions",
    line: "For trade orders, distributor accounts, retail partnerships, and co-marketing.",
    emails: ["sales@conlangbookhouse.com", "conlangbookhouse@gmail.com"],
  },
  {
    key: "custom",
    icon: BookOpen,
    name: "Customized Publishing",
    line: "For institutional, corporate, and district-sponsored volumes with bespoke editorial.",
    emails: ["editorial@conlangbookhouse.com", "conlangbookhouse@gmail.com"],
  },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inquiryTypes = [
  { id: "general", label: "General Enquiry" },
  { id: "rights", label: "Rights & Licensing" },
  { id: "media", label: "Media / Interview" },
  { id: "schools", label: "Schools & Libraries" },
];

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    subject: "",
    message: "",
    inquiry_type: searchParams.get("inquiry_type") || "general",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent. We'll reply within 3 business days.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact"
        path="/contact"
        description="Write to Conlang Book House — for editorial, rights & licensing, media, or schools and libraries. Response within 3 business days."
        keywords="CBH contact, rights and licensing India, book media enquiry"
      />
      <Toaster position="top-center" richColors />
      {/* Hero */}
      <section className="pt-16 lg:pt-24 pb-16 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ Contact</p>
          <h1 className="font-editorial text-6xl md:text-8xl leading-[0.9] tracking-tighter text-ink text-balance">
            Write to <em className="text-crimson not-italic">the house.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-ink-muted">
            For rights, media, schools & libraries, or general enquiries. We respond within
            three business days.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-12">
          {/* Desk directory */}
          <aside className="col-span-12 lg:col-span-5" data-testid="contact-desks">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ The Desks</p>
            <h2 className="font-editorial text-3xl md:text-4xl text-ink leading-tight mb-2">
              Reach the right desk.
            </h2>
            <p className="text-ink-muted text-sm mb-10">
              Every enquiry is triaged the same day. If it&rsquo;s urgent, call the Publishing Desk directly.
            </p>

            <div className="space-y-8">
              {desks.map((d) => (
                <div key={d.key} className="border-l-2 border-crimson/25 pl-6 py-1" data-testid={`desk-${d.key}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <d.icon strokeWidth={1.25} className="w-5 h-5 text-crimson" />
                    <p className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">{d.name}</p>
                  </div>
                  <p className="text-ink-muted text-sm mb-4 leading-relaxed">{d.line}</p>
                  <div className="space-y-1.5">
                    {d.emails.map((email) => (
                      <a
                        key={email}
                        href={`mailto:${email}`}
                        data-testid={`desk-${d.key}-${email.split('@')[0]}`}
                        className="block font-editorial text-lg md:text-xl text-ink hover:text-crimson link-underline break-all"
                      >
                        {email}
                      </a>
                    ))}
                  </div>
                </div>
              ))}

              {/* Publishing Desk phone */}
              <div className="mt-10 border border-crimson/25 bg-ivory-soft p-6" data-testid="desk-phone">
                <div className="flex items-center gap-3 mb-3">
                  <Phone strokeWidth={1.25} className="w-5 h-5 text-crimson" />
                  <p className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">Publishing Desk</p>
                </div>
                <a
                  href="tel:+919765395667"
                  className="font-editorial text-3xl md:text-4xl text-ink hover:text-crimson block"
                  data-testid="desk-phone-link"
                >
                  +91 97653 95667
                </a>
                <p className="text-xs text-ink-muted mt-2">Mon &ndash; Sat &middot; 10.00 to 18.30 IST</p>
              </div>
            </div>
          </aside>

          {/* Form */}
          <div className="col-span-12 lg:col-span-7">
            {sent ? (
              <div className="border border-crimson/30 bg-ivory-soft p-12" data-testid="contact-sent-panel">
                <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">Received</p>
                <h3 className="font-editorial text-4xl text-ink mb-4">Message sent.</h3>
                <p className="text-ink-muted mb-6">Thank you for writing. The relevant desk will respond within three business days.</p>
                <button onClick={() => setSent(false)} className="text-sm uppercase tracking-[0.24em] text-crimson link-underline">
                  Write another →
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Full name" required>
                    <input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className={inputCls} data-testid="contact-full-name" />
                  </Field>
                  <Field label="Email" required>
                    <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} data-testid="contact-email" />
                  </Field>
                </div>
                <Field label="Enquiry type">
                  <select value={form.inquiry_type} onChange={(e) => update("inquiry_type", e.target.value)} className={inputCls} data-testid="contact-inquiry-type">
                    {inquiryTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Subject" required>
                  <input required value={form.subject} onChange={(e) => update("subject", e.target.value)} className={inputCls} data-testid="contact-subject" />
                </Field>
                <Field label="Message" required>
                  <textarea required rows={8} value={form.message} onChange={(e) => update("message", e.target.value)} minLength={10} className={inputCls} data-testid="contact-message" />
                </Field>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="w-full bg-crimson text-ivory px-8 py-5 text-sm uppercase tracking-[0.24em] hover:bg-crimson-dark transition-colors duration-500 disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Send Message →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-ivory border border-crimson/20 px-4 py-3 text-sm focus:outline-none focus:border-crimson transition-colors duration-300";
const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.22em] text-ink-muted mb-2">
      {label} {required && <span className="text-crimson">*</span>}
    </span>
    {children}
  </label>
);
